/**
 * This is the client-side entrypoint for your tRPC API. It is used to create the `api` object which
 * contains the Next.js App-wrapper, as well as your type-safe React Query hooks.
 *
 * We also create a few inference helpers for input and output types.
 */
import { createTRPCProxyClient, httpBatchLink, loggerLink } from '@trpc/client';
import { createTRPCNext } from '@trpc/next';
import { type inferRouterInputs, type inferRouterOutputs } from '@trpc/server';
import superjson from 'superjson';
import type { AppRouter } from '@r-and-a-shared-ui/randa-trpc-server';
import { getUrl } from '../utils';

/** A set of type-safe react-query hooks for your tRPC API. */
export const api = createTRPCNext<AppRouter>({
  config: ({ ctx }: any) => ({
    /**
     * Links used to determine request flow from client to server.
     *
     * @see https://trpc.io/docs/links
     */
    links: [
      loggerLink({
        enabled: opts =>
          process.env.NODE_ENV === 'development' ||
          (opts.direction === 'down' && opts.result instanceof Error),
      }),
      httpBatchLink({
        url: getUrl(),
        maxURLLength: 2048,
        transformer: superjson,
        headers: () => {
          if (ctx?.req) {
            // on ssr, forward client's headers to the server
            return {
              ...ctx.req.headers,
              'x-ssr': '1',
            };
          }
          return {};
        },
      }),
    ],
    queryClientConfig: {
      defaultOptions: {
        queries: {
          structuralSharing: false, // disables for all tRPC queries
        },
      },
    },
  }),
  /**
   * Whether tRPC should await queries when server rendering pages.
   *
   * @see https://trpc.io/docs/nextjs#ssr-boolean-default-false
   */
  ssr: false,
} as any);

export const trpcProxyClient = createTRPCProxyClient<AppRouter>({
  links: [
    httpBatchLink({
      url: getUrl(),
      maxURLLength: 2048,
      transformer: superjson,
    }),
    loggerLink({
      enabled: opts =>
        process.env.NODE_ENV === 'development' ||
        (opts.direction === 'down' && opts.result instanceof Error),
    }),
  ],
});
/**
 * Inference helper for inputs.
 *
 * @example type HelloInput = RouterInputs['example']['hello']
 */
export type RouterInputs = inferRouterInputs<AppRouter>;

/**
 * Inference helper for outputs.
 *
 * @example type HelloOutput = RouterOutputs['example']['hello']
 */
export type RouterOutputs = inferRouterOutputs<AppRouter>;
